import {
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_PADDING_BOTTOM_ROWS,
  REPORT_PAGE_PADDING_TOP_ROWS,
} from '../../PageConfigProvider/constants';
import type { Position } from '../../PageConfigProvider/types/item';

/**
 * Height of a report page in grid rows.
 */
export const reportPageHeight = () => DEFAULT_GRID_ROWS;

/**
 * Y offset in grid rows for the top of report page `page` (0-based).
 */
export const reportPageY = (page: number): number =>
  page * reportPageHeight() + REPORT_PAGE_PADDING_TOP_ROWS;

function getReportPageBoundsForY(
  y: number,
  rowsPerPage: number = reportPageHeight()
): { start: number; end: number } {
  const pageIndex = Math.floor(y / rowsPerPage);
  const start = pageIndex * rowsPerPage;
  const end = start + rowsPerPage;
  return { start, end };
}

export function getReportInnerBoundsForY(y: number): {
  innerStart: number;
  innerEnd: number;
} {
  const rowsPerPage = DEFAULT_GRID_ROWS;
  const { start, end } = getReportPageBoundsForY(y, rowsPerPage);
  const pageIndex = start / rowsPerPage;
  const innerStart = Math.min(end, reportPageY(pageIndex));
  const innerEnd = Math.max(
    innerStart,
    end - Math.max(0, REPORT_PAGE_PADDING_BOTTOM_ROWS)
  );
  return { innerStart, innerEnd };
}

export function clampPositionToReportPage(
  position: Position,
  baselineY: number
): Position {
  const { innerStart, innerEnd } = getReportInnerBoundsForY(baselineY);
  const minY = innerStart;
  const maxY = Math.max(minY, innerEnd - position.height);
  const clampedY = Math.min(Math.max(position.y, minY), maxY);

  const maxHeight = Math.max(1, innerEnd - clampedY);
  const clampedHeight = Math.min(position.height, maxHeight);

  if (clampedY === position.y && clampedHeight === position.height) {
    return position;
  }
  return { ...position, y: clampedY, height: clampedHeight };
}
